import React, { useMemo, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Alert,
  Modal,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import type { RootStackParamList } from '../types/navigation';
import { detectInventoryDuplicates, type InventoryDuplicateGroup } from '../utils/inventoryDuplicateDetection';
import type { CacheListItemData } from '../store/slices/userProductsSlice';
import { itemValuationDisplay } from '../lib/itemValuation';
import { supabase } from '../lib/supabase';
import { useDispatch } from 'react-redux';
import { removeUserProduct, fetchUserProducts } from '../store/slices/userProductsSlice';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';

type Props = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MergeDuplicates'>;
  route: RouteProp<RootStackParamList, 'MergeDuplicates'>;
};

interface GroupState {
  group: InventoryDuplicateGroup;
  merged: boolean;
  processing: boolean;
  keptItemId?: string;
}

export default function MergeDuplicatesScreen({ navigation, route }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        centered: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing.xl,
        },
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          padding: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        backButton: {
          marginRight: theme.spacing.md,
          padding: theme.spacing.xs,
        },
        headerTextContainer: {
          flex: 1,
        },
        headerTitle: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
        },
        headerSubtitle: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.xxs,
        },
        scrollView: {
          flex: 1,
        },
        groupCard: {
          margin: theme.spacing.lg,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        groupHeader: {
          padding: theme.spacing.md,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        groupTitle: {
          ...theme.typography.lg,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        groupReason: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          marginTop: theme.spacing.xxs,
          letterSpacing: 0,
        },
        itemsContainer: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          padding: theme.spacing.md,
          gap: theme.spacing.md,
        },
        itemBox: {
          width: '47%',
          alignItems: 'center',
        },
        itemThumbnailContainer: {
          position: 'relative',
          width: '100%',
          aspectRatio: 1,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusSm,
          overflow: 'hidden',
        },
        keptItem: {
          borderColor: theme.colors.accent,
          borderWidth: 2,
        },
        deletedItem: {
          opacity: 0.4,
        },
        itemThumbnail: {
          width: '100%',
          height: '100%',
        },
        thumbnailPlaceholder: {
          backgroundColor: theme.colors.backgroundElevated,
          justifyContent: 'center',
          alignItems: 'center',
        },
        deletedOverlay: {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(13, 13, 13, 0.7)',
          justifyContent: 'center',
          alignItems: 'center',
        },
        keptBadge: {
          position: 'absolute',
          top: theme.spacing.sm,
          right: theme.spacing.sm,
          backgroundColor: theme.colors.backgroundPrimary,
          borderRadius: 12,
        },
        itemCondition: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          marginTop: theme.spacing.xs,
          letterSpacing: 0,
        },
        itemValue: {
          ...theme.typography.valueSm,
          color: theme.colors.textPrimary,
          marginTop: theme.spacing.xxs,
        },
        keepButton: {
          marginTop: theme.spacing.sm,
          paddingVertical: theme.spacing.xs,
          paddingHorizontal: theme.spacing.md,
          backgroundColor: theme.colors.accent,
          borderRadius: theme.radius.radiusMd,
        },
        keepButtonText: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.onAccent,
          letterSpacing: 0,
        },
        mergedBadge: {
          flexDirection: 'row',
          alignItems: 'center',
          padding: theme.spacing.md,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
          gap: theme.spacing.xs,
        },
        mergedText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
        },
        actions: {
          padding: theme.spacing.md,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
        },
        actionButton: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          paddingVertical: theme.spacing.md,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        separateButton: {
          backgroundColor: 'transparent',
        },
        separateButtonText: {
          ...theme.typography.sm,
          color: theme.colors.textPrimary,
        },
        loadingText: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.md,
        },
        modalButtons: {
          flexDirection: 'row',
          gap: theme.spacing.md,
          marginTop: theme.spacing.lg,
        },
        modalButton: {
          flex: 1,
        },
      }),
    [theme],
  );
  const { items } = route.params;
  const { session } = useAuth();
  const dispatch = useDispatch<any>();
  const [groups, setGroups] = useState<GroupState[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageUrls, setImageUrls] = useState<{ [itemId: string]: string }>({});
  const [processing, setProcessing] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ visible: boolean; groupIndex: number; keepItemId: string; duplicateCount: number }>({
    visible: false,
    groupIndex: -1,
    keepItemId: '',
    duplicateCount: 0,
  });

  useEffect(() => {
    initializeGroups();
    fetchImageUrls();
  }, []);

  const initializeGroups = () => {
    const detectedGroups = detectInventoryDuplicates(items);
    console.log(`[DEBUG] Detected ${detectedGroups.length} duplicate groups in inventory`);
    
    const groupStates = detectedGroups.map(group => ({
      group,
      merged: false,
      processing: false,
    }));
    
    setGroups(groupStates);
    setLoading(false);
    
    // If no duplicates detected, go back
    if (detectedGroups.length === 0) {
      Alert.alert('No Duplicates', 'No duplicate items found in your cache.', [
        { text: 'OK', onPress: () => navigation.goBack() }
      ]);
    }
  };

  const fetchImageUrls = async () => {
    const urlMap: { [itemId: string]: string } = {};
    
    for (const item of items) {
      const images = item.images as any;
      if (images && Array.isArray(images) && images.length > 0) {
        try {
          const imagePath = images[0].storage_path || images[0];
          const { data } = supabase.storage
            .from('item-images')
            .getPublicUrl(imagePath);
          
          if (data?.publicUrl) {
            urlMap[item.id] = data.publicUrl;
          }
        } catch (error) {
          console.error(`Error fetching image for item ${item.id}:`, error);
        }
      }
    }
    
    setImageUrls(urlMap);
  };

  const confirmMerge = (groupIndex: number, keepItemId: string) => {
    const groupState = groups[groupIndex];
    const { itemIds } = groupState.group;
    const duplicateCount = itemIds.length - 1;
    
    setConfirmModal({
      visible: true,
      groupIndex,
      keepItemId,
      duplicateCount,
    });
  };

  const handleConfirmMerge = () => {
    const { groupIndex, keepItemId } = confirmModal;
    setConfirmModal({ visible: false, groupIndex: -1, keepItemId: '', duplicateCount: 0 });
    handleMergeGroup(groupIndex, keepItemId);
  };

  const handleCancelMerge = () => {
    setConfirmModal({ visible: false, groupIndex: -1, keepItemId: '', duplicateCount: 0 });
  };

  const handleMergeGroup = async (groupIndex: number, keepItemId: string) => {
    const groupState = groups[groupIndex];
    const { itemIds } = groupState.group;

    setGroups(prev => prev.map((g, i) => 
      i === groupIndex ? { ...g, processing: true } : g
    ));

    try {
      console.log(`[DEBUG] Merging group, keeping item ${keepItemId}`);
      
      // Delete all other items in the group
      const itemsToDelete = itemIds.filter(id => id !== keepItemId);
      
      for (const itemId of itemsToDelete) {
        await dispatch(removeUserProduct(itemId)).unwrap();
        console.log(`[DEBUG] Deleted duplicate item ${itemId}`);
      }

      setGroups(prev => {
        const updated = prev.map((g, i) => 
          i === groupIndex ? { 
            ...g, 
            merged: true, 
            processing: false,
            keptItemId: keepItemId
          } : g
        );
        
        // Check if all groups are now merged or removed
        const allProcessed = updated.every(g => g.merged);
        if (allProcessed) {
          // All groups handled, navigate back after a short delay
          setTimeout(() => {
            handleComplete();
          }, 500);
        }
        
        return updated;
      });

    } catch (error) {
      console.error('[ERROR] Failed to merge group:', error);
      Alert.alert('Error', 'Failed to merge items. Please try again.');
      
      setGroups(prev => prev.map((g, i) => 
        i === groupIndex ? { ...g, processing: false } : g
      ));
    }
  };

  const handleKeepSeparate = (groupIndex: number) => {
    // Remove this group from the list (keep items separate)
    setGroups(prev => {
      const updated = prev.filter((_, i) => i !== groupIndex);
      
      // If no groups left, navigate back
      if (updated.length === 0) {
        setTimeout(() => {
          handleComplete();
        }, 300);
      }
      
      return updated;
    });
  };

  const handleComplete = async () => {
    try {
      // Refresh the inventory
      if (session?.user?.id) {
        await dispatch(fetchUserProducts(session.user.id)).unwrap();
      }
      
      const mergedCount = groups.filter(g => g.merged).length;
      
      if (mergedCount > 0) {
        Alert.alert(
          'Duplicates Merged', 
          `Successfully merged ${mergedCount} duplicate group${mergedCount !== 1 ? 's' : ''}.`,
          [
            { text: 'OK', onPress: () => navigation.goBack() }
          ]
        );
      } else {
        navigation.goBack();
      }
    } catch (error) {
      console.error('[ERROR] Failed to refresh inventory:', error);
      Alert.alert('Error', 'Failed to refresh inventory.', [
        { text: 'OK', onPress: () => navigation.goBack() }
      ]);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
          <Text style={styles.loadingText}>Detecting duplicates...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (groups.length === 0) {
    return null;
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <View style={styles.headerTextContainer}>
          <Text style={styles.headerTitle}>Merge Duplicates</Text>
          <Text style={styles.headerSubtitle}>
            Found {groups.length} group{groups.length !== 1 ? 's' : ''} of potential duplicates
          </Text>
        </View>
      </View>

      <ScrollView style={styles.scrollView}>
        {groups.map((groupState, groupIndex) => {
          const { group, merged, processing, keptItemId } = groupState;
          const groupItems = items.filter(item => group.itemIds.includes(item.id));

          return (
            <View key={groupIndex} style={styles.groupCard}>
              <View style={styles.groupHeader}>
                <Text style={styles.groupTitle}>
                  {group.representativeItem.brandName} {group.representativeItem.modelName || group.representativeItem.familyName}
                </Text>
                <Text style={styles.groupReason}>{group.reason}</Text>
              </View>

              {/* Item thumbnails with selection */}
              <View style={styles.itemsContainer}>
                {groupItems.map((item) => {
                  const imageUrl = imageUrls[item.id];
                  const isKept = merged && keptItemId === item.id;
                  const isDeleted = merged && keptItemId !== item.id;
                  
                  return (
                    <View key={item.id} style={styles.itemBox}>
                      <View style={[
                        styles.itemThumbnailContainer,
                        isKept && styles.keptItem,
                        isDeleted && styles.deletedItem
                      ]}>
                        {imageUrl ? (
                          <Image source={{ uri: imageUrl }} style={styles.itemThumbnail} />
                        ) : (
                          <View style={[styles.itemThumbnail, styles.thumbnailPlaceholder]}>
                            <Ionicons name="image-outline" size={32} color={theme.colors.textTertiary} />
                          </View>
                        )}
                        {isDeleted && (
                          <View style={styles.deletedOverlay}>
                            <Ionicons name="trash" size={24} color={theme.colors.textPrimary} />
                          </View>
                        )}
                        {isKept && (
                          <View style={styles.keptBadge}>
                            <Ionicons name="checkmark-circle" size={20} color={theme.colors.accent} />
                          </View>
                        )}
                      </View>
                      
                      <Text style={styles.itemCondition} numberOfLines={1}>
                        {item.condition || 'N/A'}
                      </Text>
                      <Text style={styles.itemValue} numberOfLines={1}>
                        {(() => {
                          // V3 P6: the item's own displayed value, or its state.
                          const display = itemValuationDisplay(item);
                          return display.value !== null ? `$${display.value.toFixed(2)}` : display.label;
                        })()}
                      </Text>
                      
                      {!merged && (
                        <TouchableOpacity
                          style={styles.keepButton}
                          onPress={() => confirmMerge(groupIndex, item.id)}
                          disabled={processing}
                        >
                          <Text style={styles.keepButtonText}>Keep This</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  );
                })}
              </View>

              {merged && (
                <View style={styles.mergedBadge}>
                  <Ionicons name="checkmark-circle" size={16} color={theme.colors.accent} />
                  <Text style={styles.mergedText}>Merged - Duplicates Removed</Text>
                </View>
              )}

              {/* Actions */}
              {!merged && (
                <View style={styles.actions}>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.separateButton]}
                    onPress={() => handleKeepSeparate(groupIndex)}
                    disabled={processing}
                  >
                    <Text style={styles.separateButtonText}>Keep All Separate</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>

      {/* Confirmation Modal */}
      <Modal
        visible={confirmModal.visible}
        transparent
        animationType="fade"
        onRequestClose={handleCancelMerge}
      >
        <View style={commonStyles.modalOverlay}>
          <View style={commonStyles.modalContent}>
            <Text style={commonStyles.modalTitle}>Merge Duplicates</Text>
            <Text style={commonStyles.modalText}>
              This will keep the selected item and delete {confirmModal.duplicateCount} duplicate{confirmModal.duplicateCount !== 1 ? 's' : ''}.{'\n\n'}
              This action cannot be undone.{'\n\n'}
              Continue?
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[commonStyles.secondaryButton, styles.modalButton]}
                onPress={handleCancelMerge}
              >
                <Text style={commonStyles.secondaryButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[commonStyles.destructiveButton, styles.modalButton]}
                onPress={handleConfirmMerge}
              >
                <Text style={commonStyles.destructiveButtonText}>Merge</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

